import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import APIHandler from '../utils/APIHandler';
import { useNavigate } from 'react-router-dom';

const SUGGESTIONS = [
    { icon: 'search', text: "Search for student Aryeh and show me his devices" },
    { icon: 'warning', text: "Show me all Chromebooks that haven't synced in 14 days" },
    { icon: 'group', text: "How many students are in /Students/Boys?" },
    { icon: 'person_off', text: "Show all suspended users in /Students/Girls" },
];

function TypingIndicator() {
    return (
        <div style={{ display: 'flex', gap: 4, padding: '4px 0' }}>
            {[0, 1, 2].map(i => (
                <div key={i} style={{
                    width: 8, height: 8, borderRadius: '50%', backgroundColor: '#64748b',
                    animation: `typing 1.4s infinite ${i * 0.2}s`,
                }} />
            ))}
            <style>{`@keyframes typing { 0%,60%,100% { opacity: 0.3; transform: translateY(0); } 30% { opacity: 1; transform: translateY(-4px); } }`}</style>
        </div>
    );
}

function MarkdownText({ text }) {
    const lines = (text || '').split('\n');
    return (
        <div>
            {lines.map((line, i) => {
                let content = line;
                content = content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
                content = content.replace(/\*(.+?)\*/g, '<em>$1</em>');
                if (content.match(/^[\-\*]\s/)) {
                    content = '<span style="color:#3b82f6;margin-right:6px">&#8226;</span>' + content.slice(2);
                }
                const numMatch = content.match(/^(\d+)\.\s/);
                if (numMatch) {
                    content = `<span style="color:#3b82f6;margin-right:6px;font-weight:600">${numMatch[1]}.</span>` + content.slice(numMatch[0].length);
                }
                return (
                    <div key={i} dangerouslySetInnerHTML={{ __html: content || '&nbsp;' }}
                         style={{ minHeight: line === '' ? '8px' : 'auto', padding: content.includes('&#8226;') || numMatch ? '2px 0 2px 12px' : '1px 0' }} />
                );
            })}
        </div>
    );
}

function ConfirmAction({ confirm, onExecute, onCancel, userRole }) {
    if (!confirm) return null;
    const riskColors = { low: '#22c55e', medium: '#f59e0b', high: '#ef4444' };
    const riskBg = { low: 'rgba(34,197,94,0.1)', medium: 'rgba(245,158,11,0.1)', high: 'rgba(239,68,68,0.1)' };
    const riskBorder = { low: 'rgba(34,197,94,0.3)', medium: 'rgba(245,158,11,0.3)', high: 'rgba(239,68,68,0.3)' };
    const risk = confirm.risk || 'medium';
    const canExecute = userRole === 'superadmin';

    return (
        <div style={{
            marginTop: 12, padding: 16, borderRadius: 12,
            backgroundColor: riskBg[risk], border: `1px solid ${riskBorder[risk]}`,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span className="material-icons" style={{ fontSize: 20, color: riskColors[risk] }}>warning</span>
                <strong style={{ color: riskColors[risk], fontSize: 13, textTransform: 'uppercase' }}>
                    {risk} risk action {canExecute ? '— requires confirmation' : '— superadmin only'}
                </strong>
            </div>
            <div style={{ fontSize: 13, color: '#e2e8f0', marginBottom: 8 }}>{confirm.action}</div>
            <div style={{
                padding: '8px 12px', borderRadius: 8, backgroundColor: 'rgba(0,0,0,0.3)',
                fontFamily: 'monospace', fontSize: 12, color: '#94a3b8', marginBottom: 12,
                wordBreak: 'break-all',
            }}>
                {confirm.command}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={onCancel} style={{
                    padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                    backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                    color: '#94a3b8', cursor: 'pointer',
                }}>Cancel</button>
                {canExecute ? (
                    <button onClick={() => onExecute(confirm.command)} style={{
                        padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                        backgroundColor: risk === 'high' ? '#dc2626' : risk === 'medium' ? '#d97706' : '#16a34a',
                        border: 'none', color: 'white', cursor: 'pointer',
                    }}>
                        <span className="material-icons" style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }}>play_arrow</span>
                        Yes, Execute
                    </button>
                ) : (
                    <div style={{ fontSize: 12, color: '#64748b', padding: '8px 0' }}>
                        <span className="material-icons" style={{ fontSize: 14, verticalAlign: 'middle' }}>lock</span>
                        Only superadmin can execute commands. Copy the command and run manually.
                    </div>
                )}
            </div>
        </div>
    );
}

function ActionButtons({ buttons, onAction }) {
    if (!buttons || buttons.length === 0) return null;
    return (
        <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12,
            paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)',
        }}>
            {buttons.map((btn, i) => (
                <button key={i} onClick={() => onAction(btn.query)} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: 22, fontSize: 13, fontWeight: 500,
                    backgroundColor: 'rgba(59,130,246,0.1)', color: '#60a5fa',
                    border: '1px solid rgba(59,130,246,0.25)', cursor: 'pointer',
                    transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.target.style.backgroundColor = 'rgba(59,130,246,0.25)'; e.target.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.target.style.backgroundColor = 'rgba(59,130,246,0.1)'; e.target.style.transform = 'none'; }}
                >
                    {btn.label}
                </button>
            ))}
        </div>
    );
}

function ToolChip({ tool }) {
    const icons = {
        search_student: 'search', get_student_info: 'person', find_student_by_uid: 'fingerprint',
        get_group_members: 'group', get_device_info: 'laptop', get_device_network: 'wifi',
        get_student_full_details: 'badge', get_user_groups: 'groups', list_groups: 'list',
        get_classroom_info: 'school', get_device_full_details: 'devices', find_devices_by_student: 'phonelink',
    };
    const icon = icons[tool.tool] || 'build';
    return (
        <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500,
            backgroundColor: tool.error ? 'rgba(239,68,68,0.15)' : 'rgba(59,130,246,0.15)',
            color: tool.error ? '#f87171' : '#60a5fa',
            border: `1px solid ${tool.error ? 'rgba(239,68,68,0.3)' : 'rgba(59,130,246,0.3)'}`,
        }}>
            <span className="material-icons" style={{ fontSize: 13 }}>{icon}</span>
            {tool.tool.replace(/_/g, ' ')}
            <span style={{ opacity: 0.6 }}>{tool.time}s</span>
        </div>
    );
}

function Dashboard() {
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [activeConvId, setActiveConvId] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [user, setUser] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) setUser(JSON.parse(userData));
        loadConversations();
    }, []);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const loadConversations = async () => {
        try {
            const resp = await APIHandler.makeRequest('get', '/conversations');
            if (resp.Success) setConversations(resp.conversations || []);
        } catch (e) { /* ignore */ }
    };

    const loadConversation = async (convId) => {
        try {
            const resp = await APIHandler.makeRequest('get', `/conversations/${convId}`);
            if (resp.Success) {
                setActiveConvId(convId);
                setMessages(resp.conversation.messages || []);
            }
        } catch (e) { /* ignore */ }
    };

    const startNewChat = () => {
        setActiveConvId(null);
        setMessages([]);
        inputRef.current?.focus();
    };

    const deleteConversation = async (convId, e) => {
        e.stopPropagation();
        try {
            await APIHandler.makeRequest('delete', `/conversations/${convId}`);
            setConversations(prev => prev.filter(c => c.id !== convId));
            if (activeConvId === convId) startNewChat();
        } catch (e) { /* ignore */ }
    };

    const sendMessage = async (text) => {
        if (!text.trim() || loading) return;
        const userMessage = text.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', text: userMessage }]);
        setLoading(true);

        try {
            const history = messages.map(m => ({
                role: m.role === 'assistant' ? 'model' : 'user',
                text: m.text
            }));

            const response = await APIHandler.chat(userMessage, history);
            const assistantMsg = {
                role: 'assistant',
                text: response.Success ? response.response : (response.Message || 'Something went wrong.'),
                buttons: response.buttons || [],
                confirm: response.confirm || null,
                tools: response.tools_used || [],
                time: response.total_time || 0,
                error: !response.Success
            };

            setMessages(prev => [...prev, assistantMsg]);

            // Save to conversation
            let convId = activeConvId;
            if (!convId) {
                const convResp = await APIHandler.makeRequest('post', '/conversations');
                if (convResp.Success) {
                    convId = convResp.conversation_id;
                    setActiveConvId(convId);
                }
            }
            if (convId) {
                await APIHandler.makeRequest('post', `/conversations/${convId}/messages`, {
                    user_message: userMessage,
                    assistant_message: assistantMsg.text,
                    tools_used: assistantMsg.tools,
                    total_time: assistantMsg.time,
                });
                loadConversations();
            }
        } catch (error) {
            setMessages(prev => [...prev, {
                role: 'assistant', text: 'Connection failed. Please try again.', tools: [], time: 0, error: true
            }]);
        }
        setLoading(false);
        inputRef.current?.focus();
    };

    const executeCommand = async (command) => {
        setLoading(true);
        setMessages(prev => [...prev, { role: 'user', text: `Confirmed execution: ${command}` }]);
        try {
            const resp = await APIHandler.makeRequest('post', '/chat/execute', { command, confirmed: true });
            const resultMsg = resp.Success
                ? `Command executed successfully (${resp.time}s)\n\n\`\`\`\n${resp.output || 'No output'}\n\`\`\``
                : `Command failed: ${resp.error || resp.Message}`;
            setMessages(prev => [...prev, { role: 'assistant', text: resultMsg, tools: [], time: resp.time || 0, buttons: [] }]);
        } catch (e) {
            setMessages(prev => [...prev, { role: 'assistant', text: `Execution error: ${e.message}`, error: true, tools: [], time: 0, buttons: [] }]);
        }
        setLoading(false);
    };

    const cancelCommand = (msgIndex) => {
        setMessages(prev => prev.map((m, i) => i === msgIndex ? { ...m, confirm: null } : m));
        setMessages(prev => [...prev, { role: 'assistant', text: 'Action cancelled.', tools: [], time: 0, buttons: [] }]);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        e.target.value = '';

        const formData = new FormData();
        formData.append('file', file);

        try {
            const token = APIHandler.getToken();
            const resp = await fetch('/api/chat/upload', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });
            const data = await resp.json();

            if (data.Success) {
                setUploadedFile({ id: data.file_id, name: data.filename, rows: data.total_rows, headers: data.headers });
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    text: `Uploaded **${data.filename}** (${data.total_rows} rows)\n\nColumns: ${data.headers.join(', ')}\n\nPreview (first ${Math.min(5, data.total_rows)} rows):\n${data.preview.map(r => '- ' + Object.values(r).join(', ')).join('\n')}\n\nYou can now ask me to process this file, e.g. "check which students in this file are suspended"`,
                    tools: [], time: 0, buttons: [],
                }]);
            } else {
                alert(data.Message || 'Upload failed');
            }
        } catch (err) {
            alert('Upload failed: ' + err.message);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('refresh_token');
        navigate('/');
    };

    const showWelcome = messages.length === 0;

    return (
        <div style={styles.page}>
            <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

            {/* Sidebar */}
            <div style={{ ...styles.sidebar, width: sidebarOpen ? 280 : 0, padding: sidebarOpen ? '16px 12px' : 0 }}>
                <button onClick={startNewChat} style={styles.newChatBtn}>
                    <span className="material-icons" style={{ fontSize: 18 }}>add</span>
                    New Chat
                </button>

                <div style={styles.convList}>
                    {conversations.map(conv => (
                        <div key={conv.id}
                             onClick={() => loadConversation(conv.id)}
                             style={{ ...styles.convItem, ...(activeConvId === conv.id ? styles.convItemActive : {}) }}>
                            <span className="material-icons" style={{ fontSize: 16, opacity: 0.5 }}>chat_bubble_outline</span>
                            <span style={styles.convTitle}>{conv.title}</span>
                            <button onClick={(e) => deleteConversation(conv.id, e)} style={styles.convDelete}>
                                <span className="material-icons" style={{ fontSize: 14 }}>close</span>
                            </button>
                        </div>
                    ))}
                </div>

                {user && (
                    <div style={styles.userInfo}>
                        {user.picture && <img src={user.picture} alt="" style={styles.userAvatar} />}
                        <div style={styles.userName}>{user.name || user.email}</div>
                    </div>
                )}
            </div>

            {/* Main area */}
            <div style={styles.main}>
                {/* Top bar */}
                <div style={styles.topBar}>
                    <button onClick={() => setSidebarOpen(!sidebarOpen)} style={styles.iconBtn}>
                        <span className="material-icons">{sidebarOpen ? 'menu_open' : 'menu'}</span>
                    </button>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>GAM Assistant</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => navigate('/class/default')} style={styles.navBtn}>
                            <span className="material-icons" style={{ fontSize: 18 }}>school</span>
                        </button>
                        {user && user.role === 'superadmin' && (
                            <button onClick={() => navigate('/workflows')} style={styles.navBtn}>
                                <span className="material-icons" style={{ fontSize: 18 }}>account_tree</span>
                            </button>
                        )}
                        {user && (user.role === 'admin' || user.role === 'superadmin') && (
                            <button onClick={() => navigate('/admin')} style={styles.navBtn}>
                                <span className="material-icons" style={{ fontSize: 18 }}>admin_panel_settings</span>
                            </button>
                        )}
                        <button onClick={handleLogout} style={{ ...styles.navBtn, color: '#f87171' }}>
                            <span className="material-icons" style={{ fontSize: 18 }}>logout</span>
                        </button>
                    </div>
                </div>

                <div style={styles.chatArea}>
                    {showWelcome && (
                        <div style={styles.welcome}>
                            <div style={styles.welcomeIcon}>
                                <span className="material-icons" style={{ fontSize: 48, color: '#3b82f6' }}>support_agent</span>
                            </div>
                            <h2 style={styles.welcomeTitle}>How can I help you?</h2>
                            <p style={styles.welcomeSubtitle}>Search students, devices, groups, classrooms and more.</p>
                            <div style={styles.suggestions}>
                                {SUGGESTIONS.map((s, i) => (
                                    <button key={i} onClick={() => sendMessage(s.text)} style={styles.suggestionBtn}>
                                        <span className="material-icons" style={{ fontSize: 16, opacity: 0.5 }}>{s.icon}</span>
                                        {s.text}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {!showWelcome && (
                        <div style={styles.messages}>
                            {messages.map((msg, i) => (
                                <div key={i} style={{ ...styles.messageRow, justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                                    {msg.role === 'assistant' && (
                                        <div style={styles.avatar}>
                                            <span className="material-icons" style={{ fontSize: 18, color: '#3b82f6' }}>smart_toy</span>
                                        </div>
                                    )}
                                    <div style={{ ...styles.bubble, ...(msg.role === 'user' ? styles.userBubble : styles.assistantBubble), ...(msg.error ? styles.errorBubble : {}) }}>
                                        {msg.role === 'user' ? <div>{msg.text}</div> : <MarkdownText text={msg.text} />}
                                        {msg.role === 'assistant' && !msg.error && msg.buttons && msg.buttons.length > 0 && (
                                            <ActionButtons buttons={msg.buttons} onAction={sendMessage} />
                                        )}
                                        {msg.role === 'assistant' && msg.confirm && (
                                            <ConfirmAction confirm={msg.confirm} onExecute={executeCommand} onCancel={() => cancelCommand(i)} userRole={user?.role} />
                                        )}
                                        {msg.tools && msg.tools.length > 0 && (
                                            <div style={styles.toolsRow}>
                                                {msg.tools.map((tool, j) => <ToolChip key={j} tool={tool} />)}
                                            </div>
                                        )}
                                        {msg.time > 0 && (
                                            <div style={styles.timeRow}>
                                                <span className="material-icons" style={{ fontSize: 12 }}>timer</span>
                                                {msg.time}s
                                            </div>
                                        )}
                                    </div>
                                    {msg.role === 'user' && (
                                        <div style={{ ...styles.avatar, backgroundColor: '#3b82f6' }}>
                                            <span className="material-icons" style={{ fontSize: 18, color: 'white' }}>person</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {loading && (
                                <div style={{ ...styles.messageRow, justifyContent: 'flex-start' }}>
                                    <div style={styles.avatar}>
                                        <span className="material-icons" style={{ fontSize: 18, color: '#3b82f6' }}>smart_toy</span>
                                    </div>
                                    <div style={{ ...styles.bubble, ...styles.assistantBubble }}><TypingIndicator /></div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                    )}

                    <div style={styles.inputArea}>
                        {uploadedFile && (
                            <div style={styles.fileChip}>
                                <span className="material-icons" style={{ fontSize: 16 }}>description</span>
                                <span>{uploadedFile.name} ({uploadedFile.rows} rows)</span>
                                <button onClick={() => setUploadedFile(null)} style={styles.fileChipClose}>
                                    <span className="material-icons" style={{ fontSize: 14 }}>close</span>
                                </button>
                            </div>
                        )}
                        <form onSubmit={(e) => { e.preventDefault(); sendMessage(input); }} style={styles.inputForm}>
                            <div style={styles.inputWrapper}>
                                <input type="file" ref={fileInputRef} accept=".csv" onChange={handleFileUpload} style={{ display: 'none' }} />
                                <button type="button" onClick={() => fileInputRef.current?.click()} style={styles.attachBtn} title="Upload CSV file">
                                    <span className="material-icons" style={{ fontSize: 20 }}>attach_file</span>
                                </button>
                                <input ref={inputRef} type="text"
                                    placeholder="Ask about students, devices, GAM commands, or upload a CSV..."
                                    value={input} onChange={(e) => setInput(e.target.value)}
                                    disabled={loading} style={styles.inputField} autoFocus />
                                <button type="submit" disabled={loading || !input.trim()}
                                    style={{ ...styles.sendBtn, opacity: loading || !input.trim() ? 0.3 : 1 }}>
                                    {loading ? <Spinner animation="border" size="sm" style={{ width: 18, height: 18 }} />
                                        : <span className="material-icons" style={{ fontSize: 20 }}>send</span>}
                                </button>
                            </div>
                        </form>
                        <div style={styles.disclaimer}>Powered by Gemini AI. Real-time data from Google Workspace.</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const styles = {
    page: { height: '100vh', display: 'flex', backgroundColor: '#0a0f1a', fontFamily: "'Inter', -apple-system, sans-serif", color: '#e2e8f0' },
    sidebar: {
        height: '100vh', backgroundColor: '#0d1321', borderRight: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', flexDirection: 'column', gap: 8, overflow: 'hidden', transition: 'all 0.2s', flexShrink: 0,
    },
    newChatBtn: {
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', width: '100%',
        backgroundColor: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)',
        borderRadius: 10, color: '#60a5fa', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
    convList: { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 },
    convItem: {
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8,
        cursor: 'pointer', fontSize: 13, color: '#94a3b8', transition: 'all 0.15s', border: '1px solid transparent',
    },
    convItemActive: { backgroundColor: 'rgba(59,130,246,0.1)', borderColor: 'rgba(59,130,246,0.2)', color: '#e2e8f0' },
    convTitle: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    convDelete: {
        background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 2,
        borderRadius: 4, display: 'flex', opacity: 0.5,
    },
    userInfo: {
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 10px',
        borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 'auto',
    },
    userAvatar: { width: 28, height: 28, borderRadius: '50%' },
    userName: { fontSize: 12, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    topBar: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.06)', backgroundColor: 'rgba(15,23,42,0.8)',
    },
    iconBtn: { background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 6, borderRadius: 8, display: 'flex' },
    navBtn: {
        display: 'flex', alignItems: 'center', padding: 6, backgroundColor: 'transparent',
        border: 'none', color: '#94a3b8', borderRadius: 8, cursor: 'pointer',
    },
    chatArea: { flex: 1, display: 'flex', flexDirection: 'column', maxWidth: 860, width: '100%', margin: '0 auto', padding: '0 16px', overflow: 'hidden' },
    welcome: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '40px 20px' },
    welcomeIcon: {
        width: 80, height: 80, borderRadius: 24,
        background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(139,92,246,0.15))',
        display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(59,130,246,0.2)',
    },
    welcomeTitle: { fontSize: 24, fontWeight: 700, color: '#f1f5f9', margin: 0 },
    welcomeSubtitle: { fontSize: 14, color: '#64748b', margin: 0, textAlign: 'center', maxWidth: 400 },
    suggestions: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12, width: '100%', maxWidth: 560 },
    suggestionBtn: {
        display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px',
        backgroundColor: 'rgba(30,41,59,0.5)', border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 12, color: '#cbd5e1', fontSize: 13, cursor: 'pointer', textAlign: 'left',
    },
    messages: { flex: 1, overflowY: 'auto', padding: '20px 0' },
    messageRow: { display: 'flex', gap: 10, marginBottom: 16, alignItems: 'flex-start' },
    avatar: {
        width: 32, height: 32, borderRadius: 10, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)',
    },
    bubble: { maxWidth: '75%', padding: '12px 16px', borderRadius: 16, fontSize: 14, lineHeight: 1.6, wordBreak: 'break-word' },
    userBubble: { background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: 'white', borderBottomRightRadius: 4, boxShadow: '0 2px 12px rgba(59,130,246,0.25)' },
    assistantBubble: { backgroundColor: '#151d2e', color: '#e2e8f0', borderBottomLeftRadius: 4, border: '1px solid rgba(255,255,255,0.06)' },
    errorBubble: { borderColor: 'rgba(239,68,68,0.3)', backgroundColor: 'rgba(28,16,23,0.8)' },
    toolsRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 },
    timeRow: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#475569', marginTop: 8, justifyContent: 'flex-end' },
    fileChip: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 12px', marginBottom: 8, borderRadius: 20, fontSize: 12,
        backgroundColor: 'rgba(34,197,94,0.1)', color: '#4ade80',
        border: '1px solid rgba(34,197,94,0.25)',
    },
    fileChipClose: {
        background: 'none', border: 'none', color: '#4ade80', cursor: 'pointer', padding: 0,
        display: 'flex', opacity: 0.6,
    },
    attachBtn: {
        background: 'none', border: 'none', color: '#475569', cursor: 'pointer',
        padding: 4, display: 'flex', borderRadius: 8, transition: 'color 0.15s',
    },
    inputArea: { padding: '12px 0 16px', borderTop: '1px solid rgba(255,255,255,0.06)' },
    inputForm: { width: '100%' },
    inputWrapper: {
        display: 'flex', alignItems: 'center', gap: 8,
        backgroundColor: '#151d2e', border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16, padding: '4px 6px 4px 16px',
    },
    inputField: {
        flex: 1, backgroundColor: 'transparent', border: 'none', outline: 'none',
        color: '#e2e8f0', fontSize: 14, padding: '12px 0', fontFamily: "'Inter', -apple-system, sans-serif",
    },
    sendBtn: {
        width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
        border: 'none', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    },
    disclaimer: { textAlign: 'center', fontSize: 11, color: '#334155', marginTop: 8 },
};

export default Dashboard;
